/**
 * Saving Inspector's proposed PDF as the document's next version: the one
 * step behind "Apply fix" on the finding page and "Use Inspector changes" in
 * the policy page's suggestion dialog. The proposed file is downloaded through the server
 * proxy and uploaded as a new version pinned to the proposal's source
 * version, so a document that moved on in the meantime is never overwritten.
 * A retry after a failed decision reuses the version already saved.
 *
 * @module components/regulation/finding/changes/useApplyProposal
 */
import type { DocumentChangesView } from "@repo/shared/findingChanges";
import { useCallback, useState } from "react";

import { documentChangesFileUrl } from "../../../../state/findingChanges";
import { usePolicies } from "../../../../state/policies";
import { createProposalResolution } from "../../policy/proposalResolution";

/** The note recorded with a decision taken on Inspector's proposal. */
export const proposalDecisionNote = (proposal: Pick<DocumentChangesView, "id">, note: string | null) =>
  [note, `Used Inspector proposal ${proposal.id}.`].filter(Boolean).join("\n");

export function useApplyProposal(organizationId: string | null, policyId: string) {
  const { uploadVersion } = usePolicies(organizationId);
  const [saveProposal] = useState(createProposalResolution);
  /** Resolves to the saved version; rejects with a message to show. */
  return useCallback(
    (proposal: DocumentChangesView, currentVersion: number | null) =>
      saveProposal(proposal, currentVersion, async () => {
        if (!organizationId) throw new Error("No organization selected.");
        const response = await fetch(documentChangesFileUrl(organizationId, proposal, "proposed"), { credentials: "include" });
        if (!response.ok) throw new Error("The proposed PDF could not be downloaded. Try again.");
        const file = new File([await response.blob()], proposal.proposedFilename, { type: "application/pdf" });
        const uploaded = await uploadVersion(policyId, file, `Inspector proposal: ${proposal.summary}`, proposal.sourceVersion);
        if (uploaded.error !== null) throw new Error(uploaded.error);
        return uploaded.value;
      }),
    [organizationId, policyId, saveProposal, uploadVersion],
  );
}
